// src/pages/HomeContent.js
// ──────────────────────────────────────────────────────────────────────────
// Mbala Training Institute home — editorial, image-led, real content.
//   Hero → Purpose → Courses (hover list) → Pinned band → Stats
//   → How we train (cycle) → Services → CTA
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import Footer from '../components/Footer';
import logo from '../assets/star.png';
import { useSiteContent } from '../context/SiteContentContext';
import ProgramsAPI from '../api/programs';
import theme from '../theme/theme';

const Section = styled.section`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 8rem ${theme.layout.gutter};
`;

const About = styled.section`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 8rem ${theme.layout.gutter};
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 4rem;
  align-items: start;
  @media (max-width: 860px) { grid-template-columns: 1fr; gap: 2.2rem; }

  .h h2 { font-weight: 400; font-size: clamp(2rem, 4.2vw, 3.2rem); color: ${theme.color.ink}; margin-top: 1.2rem; max-width: 16ch; }
  .body .lead { font-size: 1.05rem; color: ${theme.color.inkSoft}; margin: 0; }
  .mv { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin-top: 2.4rem;
        @media (max-width: 560px) { grid-template-columns: 1fr; gap: 1.6rem; } }
  .mv h4 { font-family: ${theme.font.body}; font-size: 0.72rem; letter-spacing: 0.16em; text-transform: uppercase; color: ${theme.color.accent}; margin-bottom: 0.5rem; }
  .mv p { font-size: 0.93rem; color: ${theme.color.inkSoft}; margin: 0; line-height: 1.6; }
`;

// Courses hover-reveal
const ProgWrap = styled.section` background: ${theme.color.paperAlt}; border-block: 1px solid ${theme.color.line}; `;
const ProgInner = styled.div`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto;
  padding: 6rem ${theme.layout.gutter};
  display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 4rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 2rem; }
`;
const ProgList = styled.div` border-top: 1px solid ${theme.color.line}; `;
const Row = styled(Link)`
  display: flex; align-items: baseline; gap: 1.2rem; padding: 1.4rem 0;
  border-bottom: 1px solid ${theme.color.line};
  transition: padding-left 0.35s ${theme.ease};
  .num { font-size: 0.8rem; color: ${theme.color.inkSoft}; min-width: 28px; }
  .t { font-family: ${theme.font.display}; font-weight: 500; font-size: clamp(1.2rem, 2.2vw, 1.7rem); color: ${theme.color.ink}; flex: 1; letter-spacing: -0.02em; }
  .meta { font-size: 0.74rem; color: ${theme.color.inkSoft}; white-space: nowrap; }
  &:hover { padding-left: 14px; }
  &:hover .t { color: ${theme.color.accent}; }
`;
const Preview = styled.div`
  position: sticky; top: 120px; aspect-ratio: 4/5; border-radius: ${theme.layout.radius};
  overflow: hidden; background: ${theme.color.ink};
  @media (max-width: 900px) { display: none; }
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const ProgHead = styled.div` margin-bottom: 1.5rem; h2 { font-weight: 400; font-size: clamp(1.8rem, 4vw, 2.8rem); margin-top: 0.8rem; } `;

// Pinned band
const Band = styled.section`
  position: relative; min-height: 70vh; display: flex; align-items: center;
  background-image: ${theme.gradient.heroScrim},
    url('https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg?auto=compress&cs=tinysrgb&w=2400');
  background-size: cover; background-position: center; background-attachment: fixed; color: #fff;
`;
const BandInner = styled.div`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; width: 100%; padding: 0 ${theme.layout.gutter};
  h2 { font-weight: 400; font-size: clamp(2rem, 5vw, 3.6rem); max-width: 20ch; color: #fff; }
  p { max-width: 44ch; margin-top: 1.2rem; font-size: 1rem; font-weight: 300; color: rgba(255,255,255,0.85); }
`;

// Stats
const Stats = styled.section` background: ${theme.color.blueDeep}; color: #fff; padding: 6rem ${theme.layout.gutter}; `;
const StatsInner = styled.div`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto;
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 2rem;
  @media (max-width: 760px) { grid-template-columns: repeat(2, 1fr); gap: 2.5rem; }
  .v { font-family: ${theme.font.display}; font-weight: 400; font-size: 3rem; color: #fff; }
  .rule { width: 28px; height: 2px; background: ${theme.gradient.rule}; margin: 0.6rem 0; }
  .l { font-size: 0.82rem; color: rgba(255,255,255,0.7); }
`;

// How we train (cycle)
const Steps = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); border-top: 1px solid ${theme.color.line};
  @media (max-width: 820px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const Step = styled.div`
  padding: 2rem 1.6rem 2rem 0; border-right: 1px solid ${theme.color.line};
  &:last-child { border-right: none; }
  .n { font-family: ${theme.font.display}; font-size: 2rem; font-weight: 400; color: ${theme.color.lemon}; }
  h3 { font-weight: 500; font-size: 1.15rem; margin: 0.4rem 0 0.5rem; }
  p { font-size: 0.88rem; color: ${theme.color.inkSoft}; margin: 0; }
`;

// Services
const ServWrap = styled.section` background: ${theme.color.paperAlt}; border-block: 1px solid ${theme.color.line}; `;
const Serv = styled.div`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 6rem ${theme.layout.gutter};
  .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; margin-top: 2.5rem; border-top: 1px solid ${theme.color.line};
          @media (max-width: 820px) { grid-template-columns: 1fr 1fr; } @media (max-width: 520px) { grid-template-columns: 1fr; } }
  .cell { padding: 1.3rem 1.2rem 1.3rem 0; border-bottom: 1px solid ${theme.color.line}; font-size: 0.98rem; color: ${theme.color.ink}; }
`;

// CTA
const CTA = styled.section`
  position: relative; min-height: 60vh; display: flex; align-items: center;
  background-image: ${theme.gradient.heroScrim},
    url('https://images.pexels.com/photos/34705724/pexels-photo-34705724.jpeg?auto=compress&cs=tinysrgb&w=2400');
  background-size: cover; background-position: center; color: #fff; text-align: center;
  .inner { max-width: 720px; margin: 0 auto; padding: 0 ${theme.layout.gutter}; }
  h2 { font-weight: 400; font-size: clamp(2.2rem, 5.5vw, 4rem); color: #fff; }
  p { color: rgba(255,255,255,0.85); margin: 1rem 0 2rem; font-weight: 300; }
  a { display: inline-block; background: ${theme.color.lemon}; color: ${theme.color.onLemon};
      font-size: 0.82rem; font-weight: 700; padding: 14px 32px; border-radius: ${theme.layout.radius};
      &:hover { filter: brightness(0.96); } }
`;

const STEPS = [
  { n: '01', t: 'Planning & selection', p: 'Aptitude chat and counselling so we place you on the right course.' },
  { n: '02', t: 'Training delivery', p: '80% practical classes with qualified instructors and real tools.' },
  { n: '03', t: 'Post-training support', p: 'We support graduates into work and into starting a business.' },
  { n: '04', t: 'Monitoring & evaluation', p: 'Follow-up and escort services to track your progress.' },
];

const SERVICES = [
  'Outdoor catering for events',
  'School desks & furniture',
  'Building & construction projects',
  'Metal & sheet fabrication',
  'Poultry, crop & fish short courses',
  'Entrepreneurship & business support',
  'Pre-fabricated houses & toilets',
  'Tailoring & uniforms',
  'Psycho-social counselling',
];

// Why MTI — from the institute's 2024–2026 Strategic Plan (strengths/opportunities)
const WhyWrap = styled.section`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 6rem ${theme.layout.gutter};
  .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.6rem; margin-top: 2.5rem;
          @media (max-width: 720px) { grid-template-columns: 1fr; } }
  .w { display: flex; gap: 1rem; align-items: flex-start; }
  .w .mark { flex: none; width: 34px; height: 34px; border-radius: 50%; background: ${theme.color.lemonSoft};
             color: ${theme.color.accent}; display: grid; place-items: center; font-weight: 800; font-size: 0.85rem; }
  .w h3 { font-weight: 600; font-size: 1.1rem; }
  .w p { margin: 0.25rem 0 0; font-size: 0.93rem; color: ${theme.color.inkSoft}; }
`;
const WHY = [
  { t: 'The skills centre for the region', d: 'A skills-training home not only for Mbala but for Mpulungu, Senga Hill, Nakonde, Isoka and surrounding districts with no centre of their own.' },
  { t: 'User-friendly fees', d: 'Training at affordable, user-friendly fees — because cost should not stand between you and a skill.' },
  { t: 'Accredited trainers & assessors', d: 'Qualified, accredited trainers and assessors ensure quality training delivery and proper assessment.' },
  { t: 'CDF & sponsorship welcome', d: 'We support learners funded through the Constituency Development Fund, donors and sponsors.' },
];

// ── Enrolment reminder popup (dynamic by month) ─────────────
const PopOverlay = styled(motion.div)`
  position: fixed; inset: 0; z-index: 1000; background: rgba(8,24,60,0.55);
  display: flex; align-items: center; justify-content: center; padding: 4vh 5vw;
`;
const PopCard = styled(motion.div)`
  position: relative; width: min(440px, 100%); background: #fff; border-radius: ${theme.layout.radius};
  overflow: hidden; box-shadow: 0 40px 80px -30px rgba(0,0,0,0.55); text-align: center;
  .top { background: ${theme.color.blueDeep}; padding: 1.8rem 1.8rem 1.4rem; color: #fff; }
  .top img { height: 64px; width: 64px; object-fit: cover; border-radius: 50%; border: 1px solid rgba(255,255,255,0.2); margin: 0 auto 0.8rem; display: block; }
  .badge { display: inline-block; background: ${theme.color.lemon}; color: ${theme.color.onLemon};
           font-size: 0.66rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; padding: 4px 12px; border-radius: 100px; }
  .body { padding: 1.6rem 1.8rem 2rem; }
  .body h3 { font-weight: 500; font-size: 1.5rem; margin-bottom: 0.5rem; }
  .body p { color: ${theme.color.inkSoft}; font-size: 0.95rem; margin: 0 0 1.4rem; }
  .body .deadline { color: ${theme.color.accent}; font-weight: 600; }
  .actions { display: flex; gap: 0.7rem; justify-content: center; flex-wrap: wrap; }
  .actions a { background: ${theme.color.accent}; color: #fff; font-weight: 700; padding: 12px 24px; border-radius: ${theme.layout.radius}; }
  .actions a:hover { background: ${theme.color.accentDark}; }
  .actions button { background: none; border: none; color: ${theme.color.inkSoft}; font-family: ${theme.font.body}; font-size: 0.9rem; cursor: pointer; padding: 12px 10px; }
  .x { position: absolute; top: 12px; right: 14px; background: none; border: none; color: rgba(255,255,255,0.8); font-size: 1.3rem; cursor: pointer; line-height: 1; }
`;

function EnrolmentPopup() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem('mti_enrol_seen') === '1'; } catch (e) {}
    if (seen) return undefined;
    const t = setTimeout(() => setShow(true), 900);
    return () => clearTimeout(t);
  }, []);
  const close = () => {
    setShow(false);
    try { sessionStorage.setItem('mti_enrol_seen', '1'); } catch (e) {}
  };

  const now = new Date();
  const month = now.toLocaleString('en-US', { month: 'long' });
  const year = now.getFullYear();
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
  const deadline = `${lastDay} ${month} ${year}`;

  return (
    <AnimatePresence>
      {show && (
        <PopOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close}>
          <PopCard onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.32, ease: theme.ease }}>
            <button className="x" onClick={close} aria-label="Close">×</button>
            <div className="top">
              <img src={logo} alt="MTI" />
              <span className="badge">Now enrolling</span>
            </div>
            <div className="body">
              <h3>Enrolment is open</h3>
              <p>
                The <strong>{month} {year}</strong> intake is running now. Apply before{' '}
                <span className="deadline">{deadline}</span> to secure your place.
              </p>
              <div className="actions">
                <Link to="/apply" onClick={close}>Apply now →</Link>
                <button onClick={close}>Maybe later</button>
              </div>
            </div>
          </PopCard>
        </PopOverlay>
      )}
    </AnimatePresence>
  );
}

function HomeContent() {
  const intro = useSiteContent('intro');
  const [programs, setPrograms] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let alive = true;
    ProgramsAPI.getAll({ limit: 6 }).then((d) => { if (alive) setPrograms((d.programs || []).slice(0, 6)); });
    return () => { alive = false; };
  }, []);

  const stats = intro?.stats || [];

  return (
    <>
      <Navbar />
      <HeroSection />

      {/* Purpose + mission/vision */}
      <About>
        <div className="h">
          <span className="kicker">Who we are</span>
          <h2>A community skills hub, building people from scratch.</h2>
        </div>
        <div className="body">
          <p className="lead">{intro?.purpose}</p>
          <div className="mv">
            <div><h4>Our mission</h4><p>{intro?.mission}</p></div>
            <div><h4>Our vision</h4><p>{intro?.vision}</p></div>
          </div>
        </div>
      </About>

      {/* Courses */}
      <ProgWrap>
        <ProgInner>
          <div>
            <ProgHead><span className="kicker">Hands-on training</span><h2>Courses on offer</h2></ProgHead>
            <ProgList>
              {programs.map((p, i) => (
                <Row key={p.id} to={`/programs/${p.id}`} onMouseEnter={() => setActive(i)}>
                  <span className="num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="t">{p.title}</span>
                  <span className="meta">{p.faculty}</span>
                </Row>
              ))}
            </ProgList>
            <Link to="/programs" className="kicker" style={{ marginTop: '2rem' }}>All courses →</Link>
          </div>
          <Preview>
            {programs[active] && (
              <motion.img key={programs[active].id} src={programs[active].image} alt={programs[active].title}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} />
            )}
          </Preview>
        </ProgInner>
      </ProgWrap>

      {/* Pinned band */}
      <Band>
        <BandInner>
          <span className="kicker" style={{ color: '#fff' }}>Why skills</span>
          <h2 style={{ marginTop: '1.2rem' }}>Those with skills have a means to earn their lives with dignity.</h2>
          <p>We fight poverty through training, value addition and enterprise — not charity.</p>
        </BandInner>
      </Band>

      {/* Stats */}
      <Stats>
        <StatsInner>
          {stats.map((s) => (
            <div key={s.label}><div className="v">{s.value}</div><div className="rule" /><div className="l">{s.label}</div></div>
          ))}
        </StatsInner>
      </Stats>

      {/* How we train */}
      <Section style={{ paddingTop: '6rem', paddingBottom: '6rem' }}>
        <span className="kicker">Our approach</span>
        <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.8rem,4vw,2.8rem)', margin: '1rem 0 2.5rem' }}>How we train</h2>
        <Steps>
          {STEPS.map((s) => (
            <Step key={s.n}><div className="n">{s.n}</div><h3>{s.t}</h3><p>{s.p}</p></Step>
          ))}
        </Steps>
      </Section>

      {/* Why MTI */}
      <WhyWrap>
        <span className="kicker">Why MTI</span>
        <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.8rem,4vw,2.8rem)', marginTop: '1rem' }}>A skills centre built for the North</h2>
        <div className="grid">
          {WHY.map((w) => (
            <div className="w" key={w.t}><div className="mark">✓</div><div><h3>{w.t}</h3><p>{w.d}</p></div></div>
          ))}
        </div>
      </WhyWrap>

      {/* Services */}
      <ServWrap>
        <Serv>
          <span className="kicker">Beyond training</span>
          <h2 style={{ fontWeight: 400, fontSize: 'clamp(1.8rem,4vw,2.8rem)', marginTop: '1rem' }}>Services we offer the community</h2>
          <div className="grid">
            {SERVICES.map((s) => <div className="cell" key={s}>{s}</div>)}
          </div>
        </Serv>
      </ServWrap>

      {/* CTA */}
      <CTA>
        <div className="inner">
          <h2>Start where you are.</h2>
          <p>Intakes run throughout the year — youths, women and the community are all welcome.</p>
          <Link to="/apply">Join MTI</Link>
        </div>
      </CTA>

      <Footer />
      <EnrolmentPopup />
    </>
  );
}

export default HomeContent;
